import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, query, where, limit, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const BlogCacheContext = createContext();

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export function BlogCacheProvider({ children }) {
  const [categories, setCategories] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categoriesTimestamp, setCategoriesTimestamp] = useState(null);
  const [recentPostsTimestamp, setRecentPostsTimestamp] = useState(null);
  const [loading, setLoading] = useState(true);

  // New caches for blog pages and individual posts
  const [postsCache, setPostsCache] = useState({}); // { 'page-1-all': { data: [...], timestamp: ... }, ... }
  const [singlePostCache, setSinglePostCache] = useState({}); // { 'slug': { data: {...}, timestamp: ... }, ... }
  const [countCache, setCountCache] = useState({}); // { 'all': { count: 10, timestamp: ... }, ... }

  useEffect(() => {
    // Load initial data
    loadCategories();
    loadRecentPosts();
  }, []);

  async function loadCategories(forceRefresh = false) {
    // Check if cache is still valid
    if (!forceRefresh && categoriesTimestamp && Date.now() - categoriesTimestamp < CACHE_DURATION) {
      return categories;
    }

    try {
      const snapshot = await getDocs(collection(db, 'categories'));
      const cats = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(cats);
      setCategoriesTimestamp(Date.now());
      return cats;
    } catch (error) {
      console.error('Error loading categories:', error);
      return categories; // Return cached data on error
    }
  }

  async function loadRecentPosts(forceRefresh = false) {
    // Check if cache is still valid
    if (!forceRefresh && recentPostsTimestamp && Date.now() - recentPostsTimestamp < CACHE_DURATION) {
      return recentPosts;
    }

    try {
      const q = query(
        collection(db, 'blog_posts'),
        where('published', '==', true),
        limit(5)
      );
      const snapshot = await getDocs(q);
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by createdAt in memory
      posts.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      setRecentPosts(posts);
      setRecentPostsTimestamp(Date.now());
      setLoading(false);
      return posts;
    } catch (error) {
      console.error('Error loading recent posts:', error);
      setLoading(false);
      return recentPosts; // Return cached data on error
    }
  }

  function invalidateCategories() {
    setCategoriesTimestamp(null);
    loadCategories(true);
  }

  function invalidateRecentPosts() {
    setRecentPostsTimestamp(null);
    loadRecentPosts(true);
  }

  function invalidateAll() {
    invalidateCategories();
    invalidateRecentPosts();
    setPostsCache({});
    setSinglePostCache({});
    setCountCache({});
  }

  // Cache helpers for blog posts list
  function getCachedPosts(page, category) {
    const key = `page-${page}-${category || 'all'}`;
    const cached = postsCache[key];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  function setCachedPosts(page, category, data) {
    const key = `page-${page}-${category || 'all'}`;
    setPostsCache(prev => ({
      ...prev,
      [key]: { data, timestamp: Date.now() }
    }));
  }

  // Cache helpers for individual posts
  function getCachedPost(slug) {
    const cached = singlePostCache[slug];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  function setCachedPost(slug, data) {
    setSinglePostCache(prev => ({
      ...prev,
      [slug]: { data, timestamp: Date.now() }
    }));
  }

  // Cache helpers for counts
  function getCachedCount(category) {
    const key = category || 'all';
    const cached = countCache[key];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.count;
    }
    return null;
  }

  function setCachedCount(category, count) {
    const key = category || 'all';
    setCountCache(prev => ({
      ...prev,
      [key]: { count, timestamp: Date.now() }
    }));
  }

  const value = {
    categories,
    recentPosts,
    loading,
    loadCategories,
    loadRecentPosts,
    invalidateCategories,
    invalidateRecentPosts,
    invalidateAll,
    // New cache methods
    getCachedPosts,
    setCachedPosts,
    getCachedPost,
    setCachedPost,
    getCachedCount,
    setCachedCount
  };

  return (
    <BlogCacheContext.Provider value={value}>
      {children}
    </BlogCacheContext.Provider>
  );
}

export function useBlogCache() {
  const context = useContext(BlogCacheContext);
  if (!context) {
    throw new Error('useBlogCache must be used within a BlogCacheProvider');
  }
  return context;
}
